/**
 * Reads `skidaCrmChannelStats`. Read-only by design — see the type.
 *
 * Six documents at most, so the whole collection is fetched and kept live. The
 * screen always renders all six channels in `CHANNELS` order whether a document
 * exists or not, which is why this returns a map rather than a list: a missing
 * channel is a state to draw, not a row to omit.
 */
import { collection, onSnapshot, query } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { toIsoOrNull } from '@/shared/util/dates'
import { CHANNELS, type Channel } from '@/types/posts'
import type { ChannelStats } from '@/types/channelStats'

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const str = (v: unknown) => (typeof v === 'string' ? v : '')

export type ChannelStatsMap = Partial<Record<Channel, ChannelStats>>

export function subscribeToChannelStats(
  onData: (stats: ChannelStatsMap) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, CRM_COLLECTIONS.channelStats)),
    (snap) => {
      const out: ChannelStatsMap = {}
      for (const d of snap.docs) {
        // The document id is the channel. An id that is not one of ours is a
        // collector writing a channel this build does not know about — skipped
        // rather than rendered, so a rename cannot produce a seventh card.
        if (!(CHANNELS as readonly string[]).includes(d.id)) continue
        const v = d.data()
        out[d.id as Channel] = {
          channel: d.id as Channel,
          followers: num(v.followers),
          followersChange30d: num(v.followersChange30d),
          views30d: num(v.views30d),
          posts30d: num(v.posts30d),
          profileUrl: str(v.profileUrl),
          samples: Array.isArray(v.samples)
            ? (v.samples as { at?: unknown; followers?: unknown }[])
                .filter((x) => typeof x.at === 'number' && typeof x.followers === 'number')
                .map((x) => ({ at: x.at as number, followers: x.followers as number }))
                .sort((a, b) => a.at - b.at)
            : [],
          fetchedAt: toIsoOrNull(v.fetchedAt),
          error: str(v.error),
        }
      }
      onData(out)
    },
    (e) => onError(e as Error),
  )
}
